<template>
  <AnswerTextItem
    v-if="question.displayType === DisplayType.TEXT"
    :class="answerClasses"
    :answer="item"
    :answer-layout="question.answerLayout.state"
    :tooltip="question.pausedLabel"
    @selectedAnswer="onSelect"
  >
    <slot></slot>
  </AnswerTextItem>
  <AnswerImageItem
    v-if="!layout?.image && imageType"
    :class="answerClasses"
    :answer="item"
    :button-vote="settings.voteType === VoteType.BUTTON"
    :tooltip="question.pausedLabel"
    @selectedAnswer="onSelect"
  >
    <slot></slot>
  </AnswerImageItem>
  <AnswerLayoutItem
    v-if="
      (question.displayType === DisplayType.IMAGE || question.displayType === DisplayType.IMAGE_HEADLINE) &&
      layout?.image
    "
    :class="answerClasses"
    :answer="item"
    :tooltip="question.pausedLabel"
    :height="questionImageSize.height"
    :width="questionImageSize.width"
    :use-background="question.displayType === DisplayType.IMAGE"
    @selectedAnswer="onSelect"
  >
    <slot></slot>
  </AnswerLayoutItem>
  <AnswerVideoItem
    v-if="question.displayType === DisplayType.VIDEO"
    :class="answerClasses"
    :answer="item"
    :width="question.videoWidth"
    :button-label="settings.answerBtn"
    :close-color="question.videoPopoverCloseColor"
    :video-background-color="question.videoPopoverBg"
    :button-enable="question.videoButtonEnable"
    :tooltip="question.pausedLabel"
    @selectedAnswer="onSelect"
  >
    <slot></slot>
  </AnswerVideoItem>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent, reactive, ref } from 'vue';
import { DisplayType, VoteType, ResultType } from '@/src/components/games/poll/Enums';
import type {
  GamePollAnswerState,
  GamePollGeneralState,
  GamePollLayoutState,
  GamePollQuestionState
} from '@/src/components/games/poll/Model';
import { getYouTubeVideoIdFromUrl } from '@/src/utilities/Utilities';
import AnswerTextItem from '@/src/components/games/poll/AnswerTextItem.vue';
import AnswerImageItem from '@/src/components/games/poll/AnswerImageItem.vue';
import AnswerLayoutItem from '@/src/components/games/poll/AnswerLayoutItem.vue';
import AnswerVideoItem from '@/src/components/games/poll/AnswerVideoItem.vue';
import type { AnswerLayoutSettingsState } from '@/src/models/settings/AnswerLayoutSettingsModel';

export default defineComponent({
  name: 'AnswerItem',
  components: {
    AnswerTextItem,
    AnswerImageItem,
    AnswerLayoutItem,
    AnswerVideoItem
  },
  props: {
    item: {
      type: Object as PropType<GamePollAnswerState>,
      required: true
    },
    answerLayout: {
      type: Object as PropType<AnswerLayoutSettingsState>
    },
    answerClasses: {
      type: Array as PropType<string[]>
    },
    settings: {
      type: Object as PropType<GamePollGeneralState>,
      required: true
    },
    question: {
      type: Object as PropType<GamePollQuestionState>,
      required: true
    },
    layout: {
      type: Object as PropType<GamePollLayoutState>
    },
    questionImageSize: {
      type: Object as PropType<{ width: number; height: number }>,
      required: true
    }
  },
  emits: ['onSelect'],
  setup(props, context) {
    const popoverOpen = ref(false);
    const originUrl = window.location.href;
    const isError = ref(false);

    const imageSize = reactive({
      width: 0,
      height: 0
    });

    const imageType = computed(() => {
      return (
        props.question.displayType === DisplayType.IMAGE ||
        props.question.displayType === DisplayType.IMAGE_HEADLINE ||
        props.question.displayType === DisplayType.IMAGE_DESCRIPTION
      );
    });

    const onSelect = (answer: GamePollAnswerState) => {
      const showConfirm =
        !props.question.multiAnswer &&
        props.question.displayType !== DisplayType.VIDEO &&
        props.settings?.showConfirmPopup;

      context.emit('onSelect', answer, showConfirm);
    };

    const onPreloadImage = async (imageSrc: string): Promise<HTMLImageElement> => {
      return new Promise<HTMLImageElement>((resolve, reject): HTMLImageElement => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = imageSrc;
        return image;
      });
    };

    let preloadPromiseResolve: (() => void) | undefined;

    const preloadPromise = new Promise<void>((resolve) => {
      preloadPromiseResolve = resolve;
    });

    if (imageType.value) {
      onPreloadImage(props.item.value)
        .then((value) => {
          imageSize.width = value.naturalWidth;
          imageSize.height = value.naturalHeight;
          if (preloadPromiseResolve) {
            preloadPromiseResolve();
          }
        })
        // resolve even if we can't find image
        .catch(() => {
          if (preloadPromiseResolve) {
            preloadPromiseResolve();
          }
        });
    }

    if (preloadPromiseResolve) {
      preloadPromiseResolve();
    }

    return {
      onSelect,
      imageType,
      imageSize,
      popoverOpen,
      DisplayType,
      VoteType,
      originUrl,
      getYouTubeVideoIdFromUrl,
      isError,
      ResultType,
      onBeforeEnter: async () => {
        await preloadPromise;
      }
    };
  }
});
</script>
